'use client';

import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import type { Editor } from '@tiptap/react';

// --- Hooks ---
import { useTiptapEditor } from 'shared-components/hooks/use-tiptap-editor';
import { useIsBreakpoint } from 'shared-components/hooks/use-is-breakpoint';

// --- Lib ---
import {
  isExtensionAvailable,
  uploadImageWithPlaceholder,
  validateImageSize,
} from 'shared-components/lib/tiptap-utils';

import type { UploadFunction } from 'shared-components/components/TipTap/tiptap-node/image-upload-node/image-upload-node-extension';

// --- Icons ---
import { ImagePlusIcon } from 'shared-components/components/TipTap/tiptap-icons/image-plus-icon';

export const IMAGE_UPLOAD_SHORTCUT_KEY = 'mod+shift+i';

/**
 * Configuration for the image upload functionality
 */
export interface UseImageUploadConfig {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether the button should hide when insertion is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Callback function called after a successful image insertion.
   */
  onInserted?: () => void;
}

/**
 * Checks if image can be inserted in the current editor state
 */
export function canInsertImage(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false;
  if (!isExtensionAvailable(editor, 'imageUpload')) return false;

  return editor.can().insertContent({ type: 'image' });
}

/**
 * Checks if image is currently active
 */
export function isImageActive(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false;
  return editor.isActive('image');
}

/**
 * Opens a native file picker, then inserts an imageLoading placeholder and uploads
 * the chosen file. The placeholder is replaced with the real image on success.
 *
 * Returns true immediately (the picker is opened asynchronously).
 */
export function insertImage(editor: Editor | null, onInserted?: () => void): boolean {
  if (!editor || !editor.isEditable) return false;
  if (!canInsertImage(editor)) return false;

  const extension = editor.extensionManager.extensions.find((e) => e.name === 'imageUpload');
  const accept: string = extension?.options?.accept ?? 'image/*';
  const maxSize: number = extension?.options?.maxSize ?? 0;
  const onError: ((err: Error) => void) | undefined = extension?.options?.onError;
  const onUploadStart: (() => void) | undefined = extension?.options?.onUploadStart;
  const onUploadEnd: (() => void) | undefined = extension?.options?.onUploadEnd;

  // Capture cursor position before the file picker dialog opens, as the selection
  // may change while the dialog is open.
  const insertPos = editor.state.selection.$from.pos;

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.style.display = 'none';

  const cleanup = () => {
    if (document.body.contains(input)) {
      document.body.removeChild(input);
    }
  };

  input.oncancel = cleanup;

  input.onchange = async () => {
    const file = input.files?.[0];
    cleanup();

    if (!file) return;

    const sizeError = validateImageSize(file, maxSize);
    if (sizeError) {
      onError?.(sizeError);
      return;
    }

    try {
      const uploadFn = extension?.options?.upload as UploadFunction | undefined;
      if (!uploadFn) throw new Error('Image upload is not configured');

      await uploadImageWithPlaceholder(editor, file, insertPos, uploadFn, onError, onUploadStart, onUploadEnd);
      onInserted?.();
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Image upload failed'));
    }
  };

  document.body.appendChild(input);
  input.click();

  return true;
}

/**
 * Determines if the image button should be shown
 */
export function shouldShowButton(props: { editor: Editor | null; hideWhenUnavailable: boolean }): boolean {
  const { editor, hideWhenUnavailable } = props;

  if (!editor || !editor.isEditable) return false;
  if (!isExtensionAvailable(editor, 'imageUpload')) return false;

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canInsertImage(editor);
  }

  return true;
}

/**
 * Custom hook that provides image functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleImageButton() {
 *   const { isVisible, handleImage } = useImage()
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleImage}>Add Image</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedImageButton() {
 *   const { isVisible, handleImage, label, isActive } = useImage({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onInserted: () => console.log('Image inserted!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleImage}
 *       aria-pressed={isActive}
 *       aria-label={label}
 *     >
 *       Add Image
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useImageUpload(config?: UseImageUploadConfig) {
  const { editor: providedEditor, hideWhenUnavailable = false, onInserted } = config || {};

  const { editor } = useTiptapEditor(providedEditor);
  const isMobile = useIsBreakpoint();
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const canInsert = canInsertImage(editor);
  const isActive = isImageActive(editor);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }));
    };

    handleSelectionUpdate();

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, hideWhenUnavailable]);

  const handleImage = useCallback(() => {
    if (!editor) return false;
    return insertImage(editor, onInserted);
  }, [editor, onInserted]);

  useHotkeys(
    IMAGE_UPLOAD_SHORTCUT_KEY,
    (event: KeyboardEvent) => {
      event.preventDefault();
      handleImage();
    },
    {
      enabled: isVisible && canInsert,
      enableOnContentEditable: !isMobile,
      enableOnFormTags: true,
    },
  );

  return {
    isVisible,
    isActive,
    handleImage,
    canInsert,
    label: 'Add image',
    shortcutKeys: IMAGE_UPLOAD_SHORTCUT_KEY,
    Icon: ImagePlusIcon,
  };
}
